import { ChevronDown, ChevronUp } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type SortDirection = 'ascending' | 'descending' | 'none'

export type TableColumn<T> = {
  /** Stable identifier, also passed to `onSortChange`. */
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  /** Right-aligns the cell and gives it tabular/lining numerals. */
  numeric?: boolean
  sortable?: boolean
  /** Only meaningful when `sortable` — drives the header's arrow + aria-sort. */
  sortDirection?: SortDirection
}

type TableProps<T> = {
  columns: Array<TableColumn<T>>
  rows: T[]
  rowKey: (row: T) => string | number
  onRowClick?: (row: T) => void
  onSortChange?: (columnKey: string) => void
  /** Rendered in place of the body when `rows` is empty. */
  emptyState?: ReactNode
}

// Generic data table (U4) for the pipeline board's list view, the applicants
// table, and the reviewer queue — one hand-built primitive per
// design-system.md's table rules: zebra-free (`--line` row dividers only),
// sticky eyebrow-style header, `--olive-tint` row hover, tabular numerals on
// numeric columns. No table/grid library exists in this codebase by design.
export function Table<T>({ columns, rows, rowKey, onRowClick, onSortChange, emptyState }: TableProps<T>) {
  const clickable = onRowClick != null

  function onRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: T) {
    if (!clickable) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onRowClick(row)
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cx('table__head-cell', column.sortable && 'table__head-cell--sortable')}
                aria-sort={column.sortable ? (column.sortDirection ?? 'none') : undefined}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1"
                    onClick={() => onSortChange?.(column.key)}
                  >
                    {column.header}
                    {column.sortDirection === 'ascending' && <ChevronUp size={14} aria-hidden="true" />}
                    {column.sortDirection === 'descending' && <ChevronDown size={14} aria-hidden="true" />}
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? emptyState != null && (
                <tr>
                  <td className="table__cell" colSpan={columns.length}>
                    {emptyState}
                  </td>
                </tr>
              )
            : rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cx('table__row', clickable && 'table__row--clickable')}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (event) => onRowKeyDown(event, row) : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={cx('table__cell', column.numeric && 'table__cell--numeric')}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  )
}
